import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import BuyerPublicNavbar from "@/components/BuyerPublicNavbar";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { syncBuyerProfileToDraft } from "@/lib/buyerProfile";

export default function BuyerAccountPage() {
  const navigate = useNavigate();
  const { user, loading, updateBuyerProfile, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [defaultCity, setDefaultCity] = useState("");
  const [pickupPoint, setPickupPoint] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "buyer") {
      navigate("/account/login?next=/account", { replace: true });
      return;
    }
    setName(user.name || "");
    setPhone(user.phone || "");
    setCompany(user.company_name || "");
    setDefaultCity(user.default_city || "");
    setPickupPoint(user.pickup_point || "");
  }, [user, loading, navigate]);

  const handleSave = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    if (phone.trim().length < 10) {
      toast.error("Enter a valid phone number");
      return;
    }
    setSaving(true);
    try {
      const u = await updateBuyerProfile({
        name: name.trim(),
        phone: phone.trim(),
        company_name: company.trim(),
        default_city: defaultCity.trim(),
        pickup_point: pickupPoint.trim(),
      });
      syncBuyerProfileToDraft(u);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save profile");
    }
    setSaving(false);
  };

  const handleLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/");
  };

  if (loading || !user || user.role !== "buyer") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-sm text-neutral-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <BuyerPublicNavbar />
      <main className="max-w-lg mx-auto px-4 py-8 sm:py-12">
        <Card className="rounded-xl border-neutral-200 shadow-sm">
          <CardHeader className="border-b border-neutral-100">
            <CardTitle className="text-lg font-bold">My details</CardTitle>
            <p className="text-sm text-neutral-500 font-normal pt-1">
              Used to pre-fill requirement and sell forms when you are signed in
            </p>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Email</label>
              <Input value={user.email || ""} disabled className="rounded-lg bg-neutral-50 border-neutral-200" />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Full name *</label>
              <Input
                data-testid="buyer-account-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-lg border-neutral-200"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Phone *</label>
              <Input
                data-testid="buyer-account-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="rounded-lg border-neutral-200"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Company</label>
              <Input
                data-testid="buyer-account-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="rounded-lg border-neutral-200"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Default city</label>
              <Input
                data-testid="buyer-account-city"
                placeholder="e.g. Mumbai"
                value={defaultCity}
                onChange={(e) => setDefaultCity(e.target.value)}
                className="rounded-lg border-neutral-200"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Default pickup / site address</label>
              <Input
                data-testid="buyer-account-pickup"
                placeholder="For sell listings"
                value={pickupPoint}
                onChange={(e) => setPickupPoint(e.target.value)}
                className="rounded-lg border-neutral-200"
              />
            </div>
            <Button
              data-testid="buyer-account-save"
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#FF7A00] hover:bg-[#e86800] text-white rounded-lg h-11 font-bold"
            >
              {saving ? "Saving…" : "Save details"}
            </Button>
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button variant="outline" className="rounded-lg flex-1" asChild>
                <Link to="/materials">Browse materials</Link>
              </Button>
              <Button
                variant="outline"
                className="rounded-lg flex-1 text-red-700 border-red-200 hover:bg-red-50"
                data-testid="buyer-account-logout"
                onClick={handleLogout}
              >
                Sign out
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
